import { api, useQuery } from '../api/client';
import { Card, ErrorNote, SeverityPill, Skeleton } from '../components/ui';

/**
 * The standard, served from the server's own constants.
 *
 * Nothing on this page is hardcoded in the browser: the thresholds, anchors and
 * triggers come from the same module the engines read, so the rubric a manager
 * reads can never drift from the rubric the audits apply.
 */
export function RubricPage() {
  const { data, error, loading, reload } = useQuery(() => api.rubric(), []);

  if (loading && !data) {
    return (
      <div className="page-body">
        <Skeleton height={200} />
      </div>
    );
  }

  return (
    <>
      <header className="page-head">
        <div className="page-head-text">
          <h1>The standard</h1>
          <p className="page-sub">
            The scoring model every audit applies, served from the engine&rsquo;s own constants.
          </p>
        </div>
      </header>

      <div className="page-body page-enter">
        <ErrorNote error={error} onRetry={reload} />
        {data && (
          <div className="stack" style={{ maxWidth: 940 }}>
            <Card title="Scoring model">
              <div className="stack">
                <div>
                  <div className="meta-label mb-2">The 1–5 scale</div>
                  <div className="checks">
                    {([5, 4, 3, 2, 1] as const).map((n) => (
                      <div className="check" key={n}>
                        <span className="check-label" style={{ width: 22 }}>
                          {n}
                        </span>
                        <span className="check-label">{data.scoreMeanings[String(n)]}</span>
                        <span className="check-detail">{data.scoreDetail[String(n)]}</span>
                      </div>
                    ))}
                    <div className="check">
                      <span className="check-label" style={{ width: 22 }}>
                        N/A
                      </span>
                      <span className="check-label">Not applicable</span>
                      <span className="check-detail">Excluded from the average.</span>
                    </div>
                  </div>
                </div>

                <div className="divider" />

                <div>
                  <div className="meta-label mb-2">Bands</div>
                  <p className="small muted">
                    Overall score is the simple average of all scored (non-N/A) categories, rounded to one decimal.
                  </p>
                  <div className="row row-wrap mt-2">
                    {data.bands.map((b) => (
                      <span key={b.band} className="pill">
                        {b.band} · {b.min}+
                      </span>
                    ))}
                    <span className="pill pill-fail">FAIL · auto-fail override</span>
                  </div>
                  <p className="small muted mt-3">
                    Any <strong>critical</strong> category scored 1 caps the band at &ldquo;Needs Work&rdquo; regardless of the
                    average. An auto-fail trigger overrides the band entirely.
                  </p>
                </div>
              </div>
            </Card>

            <Card title="The scorecard" note="★ marks a critical category" bodyless>
              {data.categories.map((c) => (
                <div className="cat" key={c.key}>
                  <div style={{ padding: '13px 16px' }}>
                    <div className="row mb-2">
                      <span className="cat-letter">{c.letter}</span>
                      <strong>{c.title}</strong>
                      {c.critical && (
                        <span className="critical-mark" title="Critical category">
                          ★
                        </span>
                      )}
                      {c.allowsNA && <span className="pill pill-sm">N/A permitted</span>}
                    </div>
                    <p className="muted small mb-2">{c.question}</p>
                    <div className="checks">
                      <div className="check">
                        <span className="check-icon check-pass">5</span>
                        <span className="check-detail">{c.anchor5}</span>
                      </div>
                      <div className="check">
                        <span className="check-icon check-fail">1</span>
                        <span className="check-detail">{c.anchor1}</span>
                      </div>
                    </div>
                    <div className="row row-wrap mt-2" style={{ gap: 4 }}>
                      {c.listenFor.map((l, i) => (
                        <span key={i} className="pill pill-sm">
                          {l}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </Card>

            <Card title="BANT — qualifying the client">
              <p className="muted mb-2">{data.bant.bookingBar}</p>
              <div className="checks">
                <div className="check">
                  <span className="check-label" style={{ width: 92 }}>
                    Budget
                  </span>
                  <span className="check-detail">
                    PASS at ${data.bant.budgetMinMonthly.toLocaleString()}/month or more on lead-gen, with the deal volume or cash
                    flow to support it. We serve ${data.bant.budgetServedRange.min.toLocaleString()}–$
                    {data.bant.budgetServedRange.max.toLocaleString()}/mo.
                  </span>
                </div>
                <div className="check">
                  <span className="check-label" style={{ width: 92 }}>
                    Authority
                  </span>
                  <span className="check-detail">
                    PASS for the owner / decision-maker, or a decision-making partner on the call. PARTIAL when a partner must
                    sign off. FAIL for a gatekeeper with no decision-maker present.
                  </span>
                </div>
                <div className="check">
                  <span className="check-label" style={{ width: 92 }}>
                    Need
                  </span>
                  <span className="check-detail">
                    PASS for a real need inside the seller-leads scope, with the opening-versus-closing split understood. FAIL for
                    criteria a campaign cannot deliver.
                  </span>
                </div>
                <div className="check">
                  <span className="check-label" style={{ width: 92 }}>
                    Time frame
                  </span>
                  <span className="check-detail">
                    PASS inside {data.bant.timeframePassDays} days. The flexible fourth factor — it shapes the status, never the
                    booking floor.
                  </span>
                </div>
              </div>
              <div className="alert alert-info mt-3">
                BANT qualifies the <strong>client</strong> (the investor). It is not the seller-lead 5 Pillars, which qualify each
                seller lead a campaign later produces and are never applied to an SDR cold call.
              </div>
            </Card>

            <Card title="Auto-fail triggers" note="A FAIL overrides the numerical score">
              <div className="stack" style={{ gap: 10 }}>
                {Object.entries(data.autoFailTriggers)
                  .sort((a, b) => a[1].n - b[1].n)
                  .map(([key, t]) => (
                    <div className="limitation" key={key} style={{ borderLeftColor: 'var(--c-fail)' }}>
                      <div className="limitation-area">
                        {t.n}. {t.label}
                      </div>
                      <div className="muted">{t.detail}</div>
                    </div>
                  ))}
              </div>
              <p className="small muted mt-3">
                A trigger only fires on an <strong>evidence-backed</strong> finding. A flag raised because something could not be
                established — no CRM notes supplied, budget never discussed, no timestamps in the recording — is still surfaced at
                its severity and still routed to the manager, but it never fails the call on its own.
              </p>
            </Card>

            <Card title="Guardrails checked on every call">
              <div className="checks">
                <div className="check">
                  <span className="check-label" style={{ width: 130 }}>
                    Canonical pricing
                  </span>
                  <span className="check-detail">
                    {data.canonicalPricing.map((p) => `$${p.toLocaleString()}`).join(' · ')} — any other figure quoted on a call is
                    flagged.
                  </span>
                </div>
                <div className="check">
                  <span className="check-label" style={{ width: 130 }}>
                    Monologue limit
                  </span>
                  <span className="check-detail">
                    ~{data.monologueLimitSeconds}s without asking a question. Past that, the call is drifting into a pitch.
                  </span>
                </div>
                <div className="check">
                  <span className="check-label" style={{ width: 130 }}>
                    Calling hours
                  </span>
                  <span className="check-detail">8am–9pm prospect local time. Computed from the call timestamp and timezone.</span>
                </div>
              </div>
            </Card>

            <Card title="KPI context">
              <div className="row row-wrap" style={{ gap: 18 }}>
                <div className="meta-item">
                  <div className="meta-label">Calls / day</div>
                  <div className="meta-value">~{data.kpi.callsPerDay}</div>
                </div>
                <div className="meta-item">
                  <div className="meta-label">Call length</div>
                  <div className="meta-value">
                    {data.kpi.callLengthMinutes.min}–{data.kpi.callLengthMinutes.max} min
                  </div>
                </div>
                <div className="meta-item">
                  <div className="meta-label">Booked / day</div>
                  <div className="meta-value">{data.kpi.bookedPerDay}</div>
                </div>
                <div className="meta-item">
                  <div className="meta-label">Done meetings / month</div>
                  <div className="meta-value">{data.kpi.doneMeetingsPerMonth}</div>
                </div>
                <div className="meta-item">
                  <div className="meta-label">Show rate</div>
                  <div className="meta-value">{data.kpi.showRatePct}%</div>
                </div>
              </div>
              <p className="small muted mt-3">
                The comp unit is a <strong>qualified &amp; completed meeting</strong>: qualified per this standard, and the client
                attended. Protecting the integrity of that definition is what the audit is for.
              </p>
            </Card>

            <Card title="Integrity flag severities">
              <div className="row row-wrap" style={{ gap: 10 }}>
                <span className="row" style={{ gap: 6 }}>
                  <SeverityPill severity="high" />
                  <span className="small muted">Gaming, dishonesty, compliance</span>
                </span>
                <span className="row" style={{ gap: 6 }}>
                  <SeverityPill severity="med" />
                  <span className="small muted">Brand and call-quality problems</span>
                </span>
                <span className="row" style={{ gap: 6 }}>
                  <SeverityPill severity="low" />
                  <span className="small muted">Brand accuracy</span>
                </span>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
